import { SessionResults } from "@/components/practitioner/champVisuel/ChampVisuelParameter";
import { useResizeObserver } from "@mantine/hooks";
import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

type MaillageType = number[][];
type SelectedCellsType = number[][];

interface LegendProps {
  currentZone: number;
  setCurrentZone: (zone: number) => void;
  deselectAllCells: () => void;
  selectedCells: SelectedCellsType;
}

const Legend = ({
  currentZone,
  setCurrentZone,
  deselectAllCells,
  selectedCells,
}: LegendProps) => {
  const legendItems = [
    { color: "bg-yellow-500", label: "Zone Frontière", zone: 1 },
    { color: "bg-red-500", label: "Zone Profonde", zone: 2 },
    { color: "bg-green-500", label: "Zone Saine", zone: 3 },
    { color: "bg-white", label: "Zone Neutre", zone: 0 },
  ];

  // Calculer le nombre de cases sélectionnées pour chaque zone
  const countCellsByZone = (zone: number) => {
    return selectedCells.flat().filter(cell => cell === zone).length;
  };

  return (
    <div className="flex justify-center mt-4 bg-black p-4 rounded-lg">
      {legendItems.map((item) => {
        const count = countCellsByZone(item.zone);
        return (
          <div
            key={item.zone}
            onClick={() => setCurrentZone(item.zone)}
            className={`flex flex-col items-center cursor-pointer mr-4 p-3 rounded ${
              currentZone === item.zone ? "border-2 border-white" : ""
            }`}
          >
            <div className="flex items-center mb-1">
              <div
                className={`${item.color} w-5 h-5 border border-white mr-2`}
              ></div>
              <span className="text-white text-sm">{item.label}</span>
            </div>
            <span className="text-white text-xs bg-gray-700 px-2 py-1 rounded">
              {count} cases
            </span>
          </div>
        );
      })}
      {/* Add the deselect all button */}
      <div
        onClick={deselectAllCells}
        className="flex flex-col items-center justify-center cursor-pointer p-3 rounded bg-gray-500 hover:bg-gray-600"
      >
        <div className="flex items-center mb-1">
          <div className="w-5 h-5 border border-white mr-2 bg-gray-500"></div>
          <span className="text-white text-sm">Déselection</span>
        </div>
        <span className="text-white text-xs">Tout effacer</span>
      </div>
    </div>
  );
};

const CanvasGrid = ({
  width,
  height,
  maillage,
  selectedCells,
  handleCellClick,
  removedRows = [],
  removedColumns = [],
}: {
  width: number;
  height: number;
  maillage: MaillageType;
  selectedCells: SelectedCellsType;
  handleCellClick: (row: number, col: number) => void;
  removedRows?: number[]; // rows we want in pink
  removedColumns?: number[]; // columns we want in pink
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext("2d");
      if (context) {
        const cellWidth = width / 16;
        const cellHeight = height / 12;

        context.clearRect(0, 0, width, height);
        maillage.forEach((row, rowIndex) => {
          row.forEach((cell, colIndex) => {
            const isRemovedRow = removedRows.includes(rowIndex);
            const isRemovedCol = removedColumns.includes(colIndex);

            if (isRemovedRow || isRemovedCol) {
              // Cells in removed row/col -> PINK
              context.fillStyle = "#006599";
            } else {
              // Otherwise, color based on cell value
              context.fillStyle =
                cell === 0
                  ? "black"
                  : cell === 1
                  ? "darkgray"
                  : cell === 2
                  ? "lightgray"
                  : cell === 3
                  ? "white"
                  : "red";
            }

            context.fillRect(
              colIndex * cellWidth,
              rowIndex * cellHeight,
              cellWidth,
              cellHeight
            );

            // if (selectedCells[rowIndex][colIndex] === 1) {
            //   context.strokeStyle = "yellow";
            //   context.lineWidth = 2;
            // } else if (selectedCells[rowIndex][colIndex] === 2) {
            //   context.strokeStyle = "red";
            //   context.lineWidth = 2;
            // } else if (selectedCells[rowIndex][colIndex] === 3) {
            //   context.strokeStyle = "green";
            //   context.lineWidth = 2;
            // } else {
            //   context.strokeStyle = "white";
            //   context.lineWidth = 1;
            // }
            // context.strokeRect(
            //   colIndex * cellWidth,
            //   rowIndex * cellHeight,
            //   cellWidth,
            //   cellHeight
            // ); // Grid lines
            const x = colIndex * cellWidth;
            const y = rowIndex * cellHeight;
            const zone = selectedCells[rowIndex][colIndex];
            let color = "white";
        if (zone > 0) {
          if (zone === 1) color = "yellow";
          if (zone === 2) color = "red";
          if (zone === 3) color = "green";

          const radius = Math.min(cellWidth, cellHeight) * 0.1;
          const padding = radius * 0.5;
          const cx = x + padding + radius;
          const cy = y + padding + radius;

          context.beginPath();
          context.fillStyle = color;
          context.arc(cx, cy, radius, 0, Math.PI * 2);
          context.fill();
        }

            if (isRemovedRow || isRemovedCol) {
              context.fillStyle = "#006599";
            }  
            else {
              context.fillStyle = "black";
            }

            context.font = "bold 16px Arial"; // Taille et famille de la police
            const text = String(cell); // Le texte à afficher
            const textWidth = context.measureText(text).width;
            const textX = colIndex * cellWidth + (cellWidth - textWidth) / 2; // Centre le texte dans la case
            const textY = rowIndex * cellHeight + cellHeight / 2 + 6; // Ajustement pour centrer verticalement
            context.fillText(text, textX, textY);
          });
        });

        // Draw the central cross
        const centerX = width / 2;
        const centerY = height / 2;
        const crossLength = Math.min(cellWidth, cellHeight) * 0.35;

        context.beginPath();
        context.strokeStyle = "blue"; // Adjust color as needed
        context.lineWidth = 4;
        context.moveTo(centerX - crossLength / 2, centerY);
        context.lineTo(centerX + crossLength / 2, centerY);
        context.stroke();

        context.beginPath();
        context.moveTo(centerX, centerY - crossLength / 2);
        context.lineTo(centerX, centerY + crossLength / 2);
        context.stroke();
      }
    }
  }, [maillage, selectedCells, width, height,  removedRows, removedColumns]);

  const handleClick = (event: { clientX: number; clientY: number }) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const col = Math.floor(x / (width / 16));
    const row = Math.floor(y / (height / 12));
    handleCellClick(row, col);
  };

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      onClick={handleClick}
      style={{ cursor: "pointer" }}
    />
  );
};

export const ModifySelectionBilan = forwardRef(
  (
    {
      mode = "selection",
      selectionData,
      resultData,
      selectionRef,
      isLegendeVisible = true,
    }: {
      mode?: string;
      selectionData: { selection: string };
      resultData: SessionResults;
      selectionRef: { current: { ZF: string[]; ZP: string[]; ZV: string[] } };
      isLegendeVisible?: boolean;
    },
    ref2
  ) => {
    const [ref, rect] = useResizeObserver();

    const [currentZone, setCurrentZone] = useState(0); // Default to Zone Neutre (Blanc)

    const [maillage, setMaillage] = useState(
      [...Array(12)].map(() => Array(16).fill(0))
    );
    const [selectedCells, setSelectedCells] = useState(
      [...Array(12)].map(() => Array(16).fill(0))
    );

    const convertPositionToIndices = (position: string) => {
      const letter = position.charAt(0);
      const number = parseInt(position.slice(1), 10);
      const col = letter.charCodeAt(0) - 65; // Convert letter to column index (A=0, B=1, ..., O=14, P=15)
      const row = number - 1; // Convert number to row index (1=0, 2=1, ..., 12=11)
      return { row, col };
    };

    useEffect(() => {
      const handleKeyPress = (event: { key: string }) => {
        if (event.key === "j") {
          downloadModifiedData();
        }
      };

      window.addEventListener("keydown", handleKeyPress);

      return () => {
        window.removeEventListener("keydown", handleKeyPress);
      };
    });

    useEffect(() => {
      if (selectionData) {
        // console.log("selectionData", selectionData);
        let jsonResults;

        try {
          if (selectionData.selection && selectionData.selection !== "null") {
            // console.log("selectionData.selection", selectionData);
            jsonResults = JSON.parse(selectionData.selection);
          } else if (
            selectionData.selection === null ||
            selectionData.selection === "null"
          ) {
            // console.log("selectionData.selection is null");
            jsonResults = null;
          }

          // console.log("jsonResults", jsonResults);

          if (jsonResults) {
            if (jsonResults.ZP || jsonResults.ZF || jsonResults.ZV) {
              // It's a selection file
              const newSelectedCells = [...Array(12)].map(() =>
                Array(16).fill(0)
              );
              jsonResults.ZP.forEach((position: string) => {
                const { row, col } = convertPositionToIndices(position);
                newSelectedCells[row][col] = 2; // Red
              });
              jsonResults.ZF.forEach((position: string) => {
                const { row, col } = convertPositionToIndices(position);
                newSelectedCells[row][col] = 1; // Yellow
              });
              jsonResults.ZV.forEach((position: string) => {
                const { row, col } = convertPositionToIndices(position);
                newSelectedCells[row][col] = 3; // Green
              });
              setSelectedCells(newSelectedCells);
            }
          } else {
            // Clear the selectedCells
            setSelectedCells([...Array(12)].map(() => Array(16).fill(0)));
          }
        } catch (error) {
          console.error("Failed to parse JSON data:", error);
        }
      }
    }, [selectionData]);

    useEffect(() => {
      if (resultData) {
        // console.log("resultData", resultData);
        let jsonResults;

        try {
          if (resultData.resultsBilan || resultData.resultsBilan !== "null") {
            // console.log("resultData.resultsBilan", resultData.resultsBilan);
            jsonResults = JSON.parse(resultData.resultsBilan);
          } else if (
            resultData.resultsBilan === null ||
            resultData.resultsBilan === "null"
          ) {
            // console.log("resultData.resultsBilan is null");
            jsonResults = null;
          }

          // console.log("jsonResults", jsonResults);

          if (jsonResults) {
            // It's a maillage file
            const newMaillage = [...Array(12)].map(() => Array(16).fill(0));
            if (jsonResults[0].reactionTime) {
              jsonResults.forEach(
                (result: {
                  position: string;
                  reactionTime: string | number;
                }) => {
                  if (result.reactionTime !== "Trop long") {
                    const { row, col } = convertPositionToIndices(
                      result.position
                    );
                    newMaillage[row][col] += 1;
                  }
                }
              );
            } else {
              jsonResults.forEach(
                (result: { position: string; successCount: string }) => {
                  const { row, col } = convertPositionToIndices(
                    result.position
                  );
                  newMaillage[row][col] = result.successCount; // Directly assign successCount to the cell
                }
              );
            }
            setMaillage(newMaillage);
          } else {
            // Clear the maillage and selectedCells
            setMaillage([...Array(12)].map(() => Array(16).fill(0)));
          }
        } catch (error) {
          console.error("Failed to parse JSON data:", error);
        }
      }
    }, [resultData]);

    const handleCellClick = (row: number, col: number) => {
      if (mode === "resultats") {
        const newMaillage = [...maillage];
        newMaillage[row][col] = (newMaillage[row][col] + 1) % 4; // Cycle through values in results mode
        setMaillage(newMaillage);
      } else {
        const newSelectedCells = [...selectedCells];
        newSelectedCells[row][col] = currentZone; // Apply the current selected zone
        setSelectedCells(newSelectedCells);
      }
    };

    const deselectAllCells = () => {
      const newSelectedCells = [...Array(12)].map(() => Array(16).fill(0)); // Set all cells to 0 (neutral)
      setSelectedCells(newSelectedCells);
    };

    const autoSelectCells = (newMaillage: MaillageType) => {
      const rows = newMaillage.length;
      const cols = newMaillage[0]?.length || 0;
    
      // Initialisation du tableau des couleurs
      const newSelectedCells = [...Array(rows)].map(() => Array(cols).fill(0));
    
      // Premier passage : colorer selon la valeur
      newMaillage.forEach((row, rowIndex) => {
        row.forEach((cell, colIndex) => {
          if (cell === 0) {
            // Rouge (2) pour valeur 0
            newSelectedCells[rowIndex][colIndex] = 2; 
          } else if (cell === 1 || cell === 2) {
            // Jaune (1) pour 1 ou 2
            newSelectedCells[rowIndex][colIndex] = 1;
          } else if (cell === 3) {
            // Vert (3) pour 3
            newSelectedCells[rowIndex][colIndex] = 3;
          }
        });
      });
    
      // Second passage : si une cellule est 3, colorer en jaune (1) ses voisines 0
      newMaillage.forEach((row, rowIndex) => {
        row.forEach((cell, colIndex) => {
          if (cell === 3) {
            // Vérifie les 8 voisins
            for (let i = -1; i <= 1; i++) {
              for (let j = -1; j <= 1; j++) {
                // Évite de rester sur la même case
                if (i === 0 && j === 0) continue;
                
                const newRowIndex = rowIndex + i;
                const newColIndex = colIndex + j;
    
                // Vérifie qu'on ne sort pas des limites
                if (
                  newRowIndex >= 0 &&
                  newRowIndex < rows &&
                  newColIndex >= 0 &&
                  newColIndex < cols
                ) {
                  // Si la cellule voisine dans le newMaillage est 0, on la colore en jaune (1)
                  if (newMaillage[newRowIndex][newColIndex] === 0) {
                    newSelectedCells[newRowIndex][newColIndex] = 1;
                  }
                }
              }
            }
          }
        });
      });
    
      // Mise à jour du state
      setSelectedCells(newSelectedCells);
    };    

    useImperativeHandle(ref2, () => ({
      triggerAutoSelectCells: () => {
        autoSelectCells(maillage);
      },
      triggerDownloadModifiedData: () => {
        downloadModifiedData();
      },
    }));

    const downloadModifiedData = () => {
      if (mode === "resultats") {
        const convertToGridNotation = (rowIndex: number, colIndex: number) => {
          const column = String.fromCharCode(65 + colIndex); // Convert index to column letter
          const row = rowIndex + 1; // Convert index to row number
          return `${column}${row}`;
        };

        const newResults: { position: string; successCount: number }[] = [];
        maillage.forEach((row, rowIndex) => {
          row.forEach((cell, colIndex) => {
            if (cell > 0) {
              newResults.push({
                position: convertToGridNotation(rowIndex, colIndex),
                successCount: cell,
              });
            }
          });
        });
        // console.log("newResults Maillage", newResults);
      } else {
        const convertToGridNotation = (rowIndex: number, colIndex: number) => {
          const column = String.fromCharCode(65 + colIndex); // Convert index to column letter
          const row = rowIndex + 1; // Convert index to row number
          return `${column}${row}`;
        };

        const newResults: {
          ZF: string[];
          ZP: string[];
          ZV: string[];
        } = {
          ZF: [],
          ZP: [],
          ZV: [],
        };

        selectedCells.forEach((row, rowIndex) => {
          row.forEach((cell, colIndex) => {
            if (cell === 1)
              newResults.ZF.push(convertToGridNotation(rowIndex, colIndex));
            if (cell === 2)
              newResults.ZP.push(convertToGridNotation(rowIndex, colIndex));
            if (cell === 3)
              newResults.ZV.push(convertToGridNotation(rowIndex, colIndex));
          });
        });
        selectionRef.current = newResults;
      }
    };

    const maxWidth = rect.width > 1400 ? 1400 : rect.width;

    return (
      <div ref={ref}>
        <CanvasGrid
          width={maxWidth}
          height={maxWidth * 0.75}
          maillage={maillage}
          selectedCells={selectedCells}
          handleCellClick={handleCellClick}
          removedRows={resultData.removedRows}
          removedColumns={resultData.removedColumns}
        />
        {isLegendeVisible && (
          <Legend
            currentZone={currentZone}
            setCurrentZone={setCurrentZone}
            deselectAllCells={deselectAllCells}
            selectedCells={selectedCells}
          />
        )}
      </div>
    );
  }
);
